import * as React from 'react';
import { Theme, StyleRules } from '@material-ui/core/styles';
import withStyles, { ClassNameMap } from '@material-ui/core/styles/withStyles';
import {
  Grid,
  Typography,
  Paper,
  List,
  Button,
  ListItem,
  // Divider,
  LinearProgress,
  Snackbar,
  // Icon,
  Tooltip,
  SnackbarContent,
  IconButton,
} from '@material-ui/core';
import { GridSize } from '@material-ui/core/Grid';
import { grey, green } from '@material-ui/core/colors';
import classNames from 'classnames';
import { Comparison as ComparisonType } from '../modules/comparison';
import { UploadFileForHighlight, UploadFilePattern } from '../modules/uploadForHighlight';
import { ApplyContent } from '../modules/apply';
import 'react-tabs/style/react-tabs.css';
import './Comparison.css';
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  Check as CheckCircleIcon,
  // Info as InfoIcon,
  // Warning as WarningIcon,
} from '@material-ui/icons';

interface Props {
  classes: ClassNameMap;
  files: (UploadFileForHighlight | null)[];
  filePatterns: UploadFilePattern[];
  goUploadHighlightPage: () => void;
  // 比較関連
  compareDocumentsForHighlight: (fileIds: number[], fileTypes: string[]) => void;
  compareDocumentsForHighlightPut: (fromData: string, toData: string) => void;
  clearComparisionMessagesForHighlight: () => void;
  comparisonForHighlight: ComparisonType | null;
  messages: string[];
  status: number;
  // TSVダウンロード関連
  downloadComparisonDocuments: (fileIds: number[], fileTypes: string[]) => Promise<void>;
  clearComparisonDownloadedMessages: () => void;
  comparisonDownload: Promise<Blob> | null;
  comparisonDownloadedErrorMessages: string[];
  // 申請関連(apply)
  contents: ApplyContent[];
  successMessages: string[];
  errorMessages: string[];
  changeApplyContent: (content: ApplyContent) => void;
  changeCategory: (index: number, content: ApplyContent) => void;
  clearApplyContents: () => void;
  submitApplyContents: (name: string, contents: ApplyContent[]) => Promise<void>;
  clearSuccessMessages: () => void;
  clearErrorMessages: () => void;
  // カテゴリ変更の保存
  compareDocumentsForHighlightSave: () => Promise<void>;
  saveStatus: number; // 保存処理結果
}

interface State {
  onDragData: string;
  isOpen: boolean;
}

const TooltipLarge = withStyles({
  tooltip: {
    fontSize: '13px',
    maxWidth: '100%',
  },
})(Tooltip);

const style = (theme: Theme): StyleRules => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing.unit * 1,
  },
  fileName: {
    textOverflow: 'ellipsis',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
  wrapper: {
    paddingLeft: theme.spacing.unit * 2,
    paddingRight: theme.spacing.unit * 2,
  },
  progress: {
    marginTop: theme.spacing.unit * 3,
    padding: theme.spacing.unit * 2,
  },
  divider: {
    backgroundColor: grey[600],
  },
  clickedItem: {
    backgroundColor: '#FFCCFF',
  },
  sameItem: {
    backgroundColor: grey[600],
  },
  check: {
    marginTop: '5px',
  },
  highlightContainer: {
    display: 'flex',
    justifyContent: 'space-evenly',
    alignItems: 'stretch',
  },
  highlightItem: {
    flex: 1,
    width: '100%',
    padding: 5,
    margin: 5,
  },
  dropArea: {
    width: '100%',
    backgroundColor: '#dcdcdc',
    height: '10px',
  },
  dropArea2: {
    width: '100%',
    backgroundColor: '#696969',
    height: '10px',
  },
  listitemStyle: {
    width: '100%',
    height: '100%',
  },
  ulStyle: {
    overflow: 'hidden',
  },
  sepalate: {
    width: '100%',
    backgroundColor: 'white',
    height: '2px',
  },
  message: {
    display: 'flex',
    alignItems: 'center',
  },
  icon: {
    fontSize: 20,
  },
  iconVariant: {
    opacity: 0.9,
    marginRight: theme.spacing.unit,
  },
  success: {
    backgroundColor: green[600],
  },
});

class Comparison extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      onDragData: '',
      isOpen: false,
    };
    this.backUploadPage = this.backUploadPage.bind(this);
    this.handleDraggableStartEvent = this.handleDraggableStartEvent.bind(this);
    this.handleDraggableEndEvent = this.handleDraggableEndEvent.bind(this);
    this.handleOnDropEvent = this.handleOnDropEvent.bind(this);
    this.handleOnDragEnterEvent = this.handleOnDragEnterEvent.bind(this);
    this.handleOnDragOverEvent = this.handleOnDragOverEvent.bind(this);
    this.handleOnDragLeaveEvent = this.handleOnDragLeaveEvent.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.dataSave = this.dataSave.bind(this);
  }

  componentDidMount() {
    const { files, filePatterns, compareDocumentsForHighlight, goUploadHighlightPage } = this.props;
    const fileIds: number[] = [];
    const fileTypes: string[] = [];
    files.forEach((file, i) => {
      if (file) {
        fileIds.push(file.id);
        fileTypes.push(filePatterns[i]);
      }
    });
    if (fileIds.length > 0) {
      if (!this.props.comparisonForHighlight) {
        compareDocumentsForHighlight(fileIds, fileTypes);
      }
    } else {
      goUploadHighlightPage();
    }
  }

  componentDidUpdate() {
    // リストの高さを揃える
    const liTag = document.getElementsByTagName('li');
    const liTagArray = Array.from(liTag);
    liTagArray.forEach(liTag => liTag.style.height = ''); // height　指定初期化（高さずれ対応）
    const leftLiTagArray = liTagArray.filter(tag => tag.id.startsWith('0_'));
    const rightLiTagArray = liTagArray.filter(tag => tag.id.startsWith('1_'));
    leftLiTagArray.forEach((leftLiTag) => {
      rightLiTagArray.forEach((rightLiTag) => {
        if (rightLiTag.id === `1${leftLiTag.id.substr(1)}`) {
          const leftLiTagHeight = leftLiTag.clientHeight;
          const rightLiTagHeight = rightLiTag.clientHeight;
          if (leftLiTagHeight < rightLiTagHeight) {
            leftLiTag.style.height = `${String(rightLiTagHeight)}px`;
          } else {
            rightLiTag.style.height = `${String(leftLiTagHeight)}px`;
          }
        }
      });
    });

    // ドロップエリアのクリア
    const dropAreaDiv = document.getElementsByClassName('dropAreaDiv') as HTMLCollectionOf<HTMLElement>;
    const dropAreaDivArray = Array.from(dropAreaDiv);
    dropAreaDivArray.forEach((divTag) => {
      divTag.style.height = '10px';
    });
  }

  backUploadPage() {
    this.props.files.splice(0);
    this.props.files.push(null);
    this.props.files.push(null);
    this.props.files.push(null);
    this.props.goUploadHighlightPage();
  }

  // ドラッグ開始
  handleDraggableStartEvent = (event: React.DragEvent<HTMLElement>) => {
    const element = (event.target as HTMLElement);
    this.setState({ onDragData: element.id });
    element.style.border = '3px';
  }

  // ドラッグ終了
  handleDraggableEndEvent = (event: React.DragEvent<HTMLElement>) => {
    const element = (event.target as HTMLElement);
    event.stopPropagation();
    event.preventDefault();
    element.style.border = '0px';
  }

  // ドロップオーバー（無効化）
  handleOnDragOverEvent = (event: React.DragEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    const div = document.getElementById('comparisonMainDiv');
    if (!div) return;

    const element = (event.target as HTMLElement);
    if (element.id === 'scrolltop') {
      div.scrollTop -= 100;
    } else if (element.id === 'scrollbottom') {
      div.scrollTop += 100;
    }
  }

  // ドロップ
  handleOnDropEvent = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    const element = (event.target as HTMLElement);
    if (this.isTarget(element.id, this.state.onDragData)) {
      // 差し替えAPI実行
      this.props.compareDocumentsForHighlightPut(this.state.onDragData, element.id);
    }
  }

  // ドラッグ対象に入る
  handleOnDragEnterEvent = (event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    const element = (event.target as HTMLElement);
    if (this.isTarget(element.id, this.state.onDragData)) {
      element.style.height = '30px';
    }
  }

  // ドラッグ対象から出る
  handleOnDragLeaveEvent = (event: React.DragEvent<HTMLElement>) => {
    const element = (event.target as HTMLElement);
    event.preventDefault();
    if (this.isTarget(element.id, this.state.onDragData)) {
      element.style.height = '10px';
    }
  }

  isTarget = (destId: string, srcId: string) => {
    return destId !== srcId &&
      destId.substring(2, destId.length) !== srcId.substring(2, destId.length) &&
      destId.substring(0, 1) === srcId.substring(0, 1);
  }

  // 保存処理
  dataSave = () => {
    const { compareDocumentsForHighlightSave } = this.props;
    compareDocumentsForHighlightSave().then(() => {
      const { saveStatus } = this.props;
      if (saveStatus !== undefined && saveStatus !== 0) {
        this.handleOpen(saveStatus);
      }
    });
  }

  handleOpen = (status: number) => {
    this.setState({ isOpen: true });
  }

  handleClose = () => {
    this.setState({ isOpen: false });
  }

  render() {
    const {
      classes,
      comparisonForHighlight,
      messages,
      status,
      saveStatus,
    } = this.props;

    const xs: GridSize | undefined = comparisonForHighlight ?
      (Math.floor(10 / comparisonForHighlight.documents.length) as GridSize) :
      undefined;

    // TODO: 暫定
    const EXCLUDE_CATEGORY = '9999';

    // tokenチェックエラーの場合、トークンを削除し、画面遷移を発生させ、ログインページに遷移
    if (status === 403 || status === 401) {
      document.cookie = 'access_token=; expires=0';
      document.cookie = 'username=; expires=0';
      this.backUploadPage();
    }

    return (
      <React.Fragment>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={this.state.isOpen}
        >
          <div>
            <SnackbarContent
              className={saveStatus === 200 ? classes.success : classes.error}
              message={
                <span className={classes.message}>
                  {saveStatus === 200 ?
                    <CheckCircleIcon className={classNames(classes.icon, classes.iconVariant)} />
                    :
                    <ErrorIcon className={classNames(classes.icon, classes.iconVariant)} />
                  }
                  {saveStatus === 200 ? '保存しました' : '保存に失敗しました'}
                </span>
              }
              action={
                <IconButton
                  key="close"
                  color="inherit"
                  className={classes.close}
                  onClick={this.handleClose}
                >
                  <CloseIcon className={classes.icon} />
                </IconButton>
              }
            />
          </div>
        </Snackbar>
        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={this.backUploadPage}>
          戻る
        </Button>
        {comparisonForHighlight ? (
          <React.Fragment>
            <div id="scrolltop" style={{ height: '15px', width: '100%' }} draggable={true} onDragOver={this.handleOnDragOverEvent}/>
            <Grid
              id="comparisonMainDiv"
              container
              className={classNames(classes.root, 'comparison-wrap')}
              style={{ overflow: 'scroll', height: '66vh', overflowX: 'hidden', borderTop: 'solid 2px #eee', borderBottom: 'solid 2px #eee', borderLeft: 'solid 2px #eee' }}
              spacing={16}>
              <Grid item xs={12}>
                <div className={classes.wrapper}>
                  <Grid container spacing={16} justify="space-between">
                    <Grid item xs={2} />
                    {comparisonForHighlight.documents.map((doc, i) => (
                      <Grid key={i} item xs={xs}>
                        <TooltipLarge title={doc.fileName}>
                          <Typography variant="h5" className={classes.fileName}>
                            {doc.fileName}
                          </Typography>
                        </TooltipLarge>
                      </Grid>
                    ))}
                  </Grid>
                </div>
              </Grid>
              {/* タグ分ループ */}
              {Object.keys(comparisonForHighlight.tags).filter((k) => {
                if (k === EXCLUDE_CATEGORY) return false;
                return true;
              }).map((k, i) => (
                <Grid key={i} item xs={12} className="grid-wrapper">
                  <Paper className={classNames(classes.paper, 'grid-outer')}>
                    <Grid
                      container
                      spacing={16}
                      justify="space-between"
                      className="grid-content"
                    >
                      {/* タグ名 */}
                      <Grid item xs={2} className="grid-content-title">
                        <Typography
                          variant="h5"
                          className="grid-content-title-h5"
                        >
                          {comparisonForHighlight.tags[k]}
                        </Typography>
                      </Grid>
                      {comparisonForHighlight.documents.map((doc, j) => (
                        <Grid key={j} item xs={xs} className="grid-content-item">
                          <Paper>
                            <List
                              className={classes.ulStyle}
                              disablePadding
                              draggable={false}
                            >
                              {doc.diff[k] && doc.diff[k].map((s, l) => (
                                <React.Fragment key={l}>
                                  {l === 0 ?
                                    <div
                                      id={`${j}_${k}_${l}`}
                                      className={classNames(classes.dropArea, 'dropAreaDiv')}
                                      onDrop={this.handleOnDropEvent}
                                      onDragOver={this.handleOnDragOverEvent}
                                      onDragEnter={this.handleOnDragEnterEvent}
                                      onDragLeave={this.handleOnDragLeaveEvent}
                                    /> :
                                    // >{`${j}_${k}_${l}`}</div> :
                                    undefined}
                                  {/* {l !== 0 ? <Divider className={classes.divider} /> :
                                    undefined} */}
                                  {/* j=左:0 右:1 k=カテゴリNo i=カテゴリID l=カテゴリ内の記事ID */}
                                  <ListItem id={`${j}_${k}_${l}_item`}
                                    className={classes.listitemStyle}
                                    draggable={true}
                                    onDragStart={this.handleDraggableStartEvent}
                                    onDragEnd={this.handleDraggableEndEvent}
                                  >
                                    {/* <p dangerouslySetInnerHTML={{ __html: `${j}_${k}_${l} : ${s}` }} /> */}
                                    <p dangerouslySetInnerHTML={{ __html: s }} />
                                  </ListItem>
                                  <div
                                    id={`${j}_${k}_${l + 1}`}
                                    className={classNames(classes.dropArea2, 'dropAreaDiv')}
                                    onDrop={this.handleOnDropEvent}
                                    onDragOver={this.handleOnDragOverEvent}
                                    onDragEnter={this.handleOnDragEnterEvent}
                                    onDragLeave={this.handleOnDragLeaveEvent}
                                  // >{`${j}_${k}_${l + 1}`}</div>
                                  />
                                  <div className={classes.sepalate} />
                                </React.Fragment>
                              ))}
                            </List>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <div id="scrollbottom" style={{ height: '15px', width: '100%' }} draggable={true} onDragOver={this.handleOnDragOverEvent}/>
            <Grid
              container
              justify="center"
              className={classNames(classes.check, 'print-nodisp')}
            >
              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.dataSave}
                >
                  保存する
                </Button>
              </Grid>
            </Grid>
          </React.Fragment>
        ) : (messages.length === 0 &&
          <div className={classes.root}>
            <LinearProgress className={classes.progress} />
          </div>
        )
        }
      </React.Fragment>
    );
  }
}

export default withStyles(style)(Comparison);



// WEBPACK FOOTER //
// ./src/components/ComparisonHighlight.tsx